import { useState, type FormEvent } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import {
  useCreatePracticeSetupLink,
  usePracticeSetupLinks,
  useRevokePracticeSetupLink,
  useTenantTeams,
} from '../api/hooks';
import type {
  PracticeAgentSetupLink,
  PracticeAgentSetupState,
  PracticeIssuedSetup,
  PracticeSetupSource,
} from '../api/types';
import {
  ConfirmDialog,
  UNAVAILABLE,
  control,
  dateText,
  practiceErrorText,
  practiceLoadError,
} from './practice-agents';

/** The Practice a setup link connects a computer to. */
export interface SetupPractice {
  id: string;
  name: string;
  tenantId: string | null;
  teamId: string | null;
  email?: string | null;
}

const STATE_TEXT: Record<PracticeAgentSetupState, string> = {
  open: 'Open',
  used: 'Used',
  expired: 'Expired',
  revoked: 'Revoked',
  locked: 'Locked after too many wrong codes',
};

const DELIVERY_TEXT: Record<string, string> = {
  sent: 'Sent',
  failed: 'Failed',
  not_sent: 'Not sent',
};

function deliveryText(status: string | null | undefined): string {
  if (!status) return 'Not emailed';
  return DELIVERY_TEXT[status] ?? status.replaceAll('_', ' ');
}

const INVITATION_TEXT: Record<string, string> = {
  invited: "Rudi invited them to the Practice's team.",
  pending: "They already had an open invitation to the Practice's team.",
  member: "They already belong to the Practice's team.",
};

/** What happened to the email, in a sentence for the operator. */
function deliverySentence(issued: PracticeIssuedSetup): string {
  if (issued.source === 'codes')
    return 'This Rudi release cannot email setup links. Give the code to the practice yourself.';
  const to = issued.email ?? 'the practice';
  if (issued.deliveryStatus === 'sent') return `Rudi emailed the link to ${to}.`;
  if (issued.deliveryStatus === 'failed')
    return `Rudi could not send the email. Send the link to ${to} yourself.`;
  return `Rudi did not send an email. Send the link to ${to} yourself.`;
}

async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error(`Could not copy the ${what.toLowerCase()}. Select it and copy it by hand.`);
  }
}

/** A Practice's setup links, newest first, with a revoke action for open ones. */
export function SetupLinkList({ practiceId }: { practiceId: string }) {
  const links = usePracticeSetupLinks(practiceId);
  const revoke = useRevokePracticeSetupLink();
  const [pending, setPending] = useState<{
    link: PracticeAgentSetupLink;
    source: PracticeSetupSource;
  } | null>(null);

  if (links.isLoading) return <p className="text-sm text-muted-foreground">Loading setup links…</p>;
  if (links.error) {
    const error = practiceLoadError(links.error);
    return (
      <p role="alert" className="text-sm text-destructive">
        {error?.message === UNAVAILABLE ? UNAVAILABLE : `Setup links: ${error?.message}`}
      </p>
    );
  }
  const data = links.data;
  if (!data || data.items.length === 0)
    return <p className="text-sm text-muted-foreground">No setup links for this Practice yet.</p>;

  const confirm = async () => {
    if (!pending) return;
    try {
      await revoke.mutateAsync({ id: pending.link.id, source: pending.source });
      toast.success('Setup link revoked');
      setPending(null);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not revoke the setup link'));
    }
  };

  return (
    <>
      <div className="overflow-x-auto rounded-3xl border">
        <Table aria-label="Setup links">
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Email delivery</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map(link => (
              <TableRow key={link.id}>
                <TableCell>{link.email ?? 'No email'}</TableCell>
                <TableCell>
                  <Badge variant={link.state === 'open' ? 'default' : 'outline'}>
                    {STATE_TEXT[link.state] ?? link.state}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {data.source === 'codes' ? 'Not emailed' : deliveryText(link.deliveryStatus)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {dateText(link.createdAt)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {dateText(link.expiresAt)}
                </TableCell>
                <TableCell className="text-right">
                  {link.state === 'open' && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Revoke setup link for ${link.email ?? 'no email'}`}
                      onClick={() => setPending({ link, source: data.source })}
                    >
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ConfirmDialog
        open={!!pending}
        title="Revoke setup link"
        description="The link and its code stop working at once. A computer already connected with it stays connected."
        confirmLabel={revoke.isPending ? 'Revoking…' : 'Revoke link'}
        pending={revoke.isPending}
        destructive
        onConfirm={() => void confirm()}
        onClose={() => setPending(null)}
      />
    </>
  );
}

function TeamPicker({
  tenantId,
  value,
  onChange,
}: {
  tenantId: string;
  value: string;
  onChange: (teamId: string) => void;
}) {
  const teams = useTenantTeams(tenantId);
  const active = (teams.data ?? []).filter(team => team.status === 'active');
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      Team
      <select
        aria-label="Team"
        className={`${control} w-full`}
        required
        value={value}
        disabled={teams.isLoading}
        onChange={event => onChange(event.target.value)}
      >
        <option value="">{teams.isLoading ? 'Loading teams…' : 'Choose a team'}</option>
        {active.map(team => (
          <option key={team.id} value={team.id}>
            {team.displayName || team.name}
          </option>
        ))}
      </select>
      <span>This Practice has no team yet. Pick the team it belongs to.</span>
    </label>
  );
}

function IssuedSetup({ issued }: { issued: PracticeIssuedSetup }) {
  return (
    <div className="space-y-4">
      <p role="status" className="rounded-3xl border bg-card p-3 text-sm">
        Copy what you need now. Cortex shows the code{issued.url ? ' and the link' : ''} only once.
      </p>
      {issued.url && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Setup link</div>
          <div className="flex items-center gap-2">
            <code
              aria-label="Setup link"
              className="min-w-0 flex-1 break-all rounded-3xl border bg-muted px-3 py-2 text-xs"
            >
              {issued.url}
            </code>
            <Button
              size="sm"
              variant="outline"
              aria-label="Copy link"
              onClick={() => void copy(issued.url!, 'Link')}
            >
              <Copy className="h-4 w-4" />
              Copy
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-1">
        <div className="text-xs text-muted-foreground">Setup code</div>
        <div className="flex items-center gap-2">
          <code
            aria-label="Setup code"
            className="flex-1 rounded-3xl border bg-muted px-3 py-2 font-mono text-base tracking-widest"
          >
            {issued.code}
          </code>
          <Button
            size="sm"
            variant="outline"
            aria-label="Copy code"
            onClick={() => void copy(issued.code, 'Code')}
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
        </div>
      </div>
      <p className="text-sm">
        {deliverySentence(issued)}
        {issued.invitation && ` ${INVITATION_TEXT[issued.invitation] ?? ''}`}
      </p>
      <p className="text-sm text-muted-foreground">
        It works once and expires {dateText(issued.expiresAt)}.
      </p>
    </div>
  );
}

/**
 * Create a setup link for one Practice. Rudi emails it with an invitation to
 * the Practice's team. The code and link appear once, in this dialog, and
 * are gone when it closes.
 */
export function SetupLinkDialog({
  practice,
  open,
  onClose,
  showLinks = true,
}: {
  practice: SetupPractice;
  open: boolean;
  onClose: () => void;
  showLinks?: boolean;
}) {
  const create = useCreatePracticeSetupLink();
  const [email, setEmail] = useState(practice.email ?? '');
  const [teamId, setTeamId] = useState('');
  const [language, setLanguage] = useState<'' | 'de' | 'en'>('');
  const [issued, setIssued] = useState<PracticeIssuedSetup | null>(null);
  const needsTeam = !practice.teamId;
  const blocked = needsTeam && !practice.tenantId;

  const close = () => {
    if (create.isPending) return;
    setIssued(null);
    setEmail(practice.email ?? '');
    setTeamId('');
    setLanguage('');
    create.reset();
    onClose();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (blocked || create.isPending) return;
    try {
      const result = await create.mutateAsync({
        practiceId: practice.id,
        email: email.trim(),
        ...(needsTeam && teamId ? { teamId } : {}),
        ...(language ? { language } : {}),
      });
      setIssued(result);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not create the setup link'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{issued ? 'Setup link created' : 'Create setup link'}</DialogTitle>
          <DialogDescription>
            {practice.name}. The link connects one computer to this Practice, works once and expires
            after 7 days.
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <IssuedSetup issued={issued} />
        ) : (
          <form id="setup-link-form" className="space-y-4" onSubmit={event => void submit(event)}>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Email
              <Input
                aria-label="Email"
                type="email"
                required
                autoComplete="off"
                value={email}
                onChange={event => setEmail(event.target.value)}
              />
              <span>Rudi sends the link here and invites this person to the Practice's team.</span>
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Email language
              <select
                aria-label="Email language"
                className={`${control} w-full`}
                value={language}
                onChange={event => setLanguage(event.target.value as '' | 'de' | 'en')}
              >
                <option value="">Their profile language, else German</option>
                <option value="de">German</option>
                <option value="en">English</option>
              </select>
            </label>
            {needsTeam && practice.tenantId && (
              <TeamPicker tenantId={practice.tenantId} value={teamId} onChange={setTeamId} />
            )}
            {blocked && (
              <p role="alert" className="text-sm text-destructive">
                This Practice has no tenant in Rudi, so no computer can connect to it yet.
              </p>
            )}
          </form>
        )}

        {showLinks && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Setup links for this Practice</h3>
            <SetupLinkList practiceId={practice.id} />
          </div>
        )}

        <DialogFooter>
          {issued ? (
            <Button onClick={close}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close} disabled={create.isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="setup-link-form"
                disabled={blocked || create.isPending || !email.trim() || (needsTeam && !teamId)}
              >
                {create.isPending ? 'Creating…' : 'Create setup link'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
